import { useEffect, useState } from "react";
import {
  api,
  ApiError,
  type AdminPreviewResponse,
} from "../api";
import { navigate, routes } from "../router";

interface Props {
  token: string;
}

type State =
  | { kind: "loading" }
  | { kind: "preview"; entry: AdminPreviewResponse }
  | { kind: "submitting"; action: "approve" | "reject"; entry: AdminPreviewResponse }
  | { kind: "done"; result: "approved" | "rejected"; entry: AdminPreviewResponse }
  | { kind: "expired" }
  | { kind: "invalid" }
  | { kind: "consumed"; entry: AdminPreviewResponse | null }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

/**
 * The approval confirmation page that the magic-link in the admin email
 * points at. Critically: this is a GET-only page. The actual mutation
 * happens behind a button click that fires a POST. Email-client preview
 * prefetchers can hit this URL all they want — the worst they trigger
 * is the read-only preview call.
 */
export function AdminApprove({ token }: Props) {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ kind: "invalid" });
      return;
    }
    let cancelled = false;
    api
      .adminPreview(token)
      .then((entry) => {
        if (cancelled) return;
        // If the entry is already in a terminal state, surface the
        // consumed view instead of the action buttons.
        if (entry.status !== "pending") {
          setState({ kind: "consumed", entry });
        } else {
          setState({ kind: "preview", entry });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          if (e.status === 401) {
            // Both expired and tampered map to 401; we don't have
            // enough granularity in ApiError today to distinguish.
            // Default to "expired" copy because that's the more
            // common case for the admin opening an old email.
            setState({ kind: "expired" });
            return;
          }
          if (e.status === 404) {
            setState({ kind: "not_found" });
            return;
          }
        }
        setState({
          kind: "error",
          message: e instanceof Error ? e.message : String(e),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(action: "approve" | "reject", entry: AdminPreviewResponse) {
    setState({ kind: "submitting", action, entry });
    try {
      const r = await api.adminApprove(token, action);
      setState({ kind: "done", result: r.now, entry });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setState({ kind: "consumed", entry });
        return;
      }
      setState({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Approve access</h2>
      <Body state={state} onSubmit={submit} />
    </div>
  );
}

function Body({
  state,
  onSubmit,
}: {
  state: State;
  onSubmit: (action: "approve" | "reject", entry: AdminPreviewResponse) => void;
}) {
  if (state.kind === "loading") return <p className="muted">Loading…</p>;
  if (state.kind === "invalid")
    return <p className="error">This approval link is malformed (missing token).</p>;
  if (state.kind === "expired")
    return (
      <>
        <p>This approval link has expired or is invalid.</p>
        <p className="muted">
          The user can re-attempt sign-in to generate a new request.
        </p>
      </>
    );
  if (state.kind === "not_found")
    return (
      <p>
        Request not found. The user may have been approved or rejected through
        a different link.
      </p>
    );
  if (state.kind === "error")
    return <div className="error">{state.message}</div>;

  if (state.kind === "consumed") {
    const entry = state.entry;
    return (
      <>
        {entry && <RequestCard entry={entry} />}
        <p>
          This request has already been{" "}
          <strong>{entry?.status ?? "decided"}</strong>. The link has been
          consumed.
        </p>
        <p style={{ marginTop: 16 }}>
          <button className="secondary" onClick={() => navigate(routes.home())}>
            Open recspec
          </button>
        </p>
      </>
    );
  }

  if (state.kind === "done") {
    return (
      <>
        <RequestCard entry={state.entry} />
        <p>
          {state.result === "approved" ? (
            <>
              <strong>@{state.entry.gh_login}</strong> is now approved. We've
              emailed them.
            </>
          ) : (
            <>
              <strong>@{state.entry.gh_login}</strong>'s request was rejected.
            </>
          )}
        </p>
        <p style={{ marginTop: 16 }}>
          <button className="secondary" onClick={() => navigate(routes.home())}>
            Open recspec
          </button>
        </p>
      </>
    );
  }

  // preview or submitting
  const entry = state.entry;
  const submitting = state.kind === "submitting";
  return (
    <>
      <RequestCard entry={entry} />
      <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
        <button
          className="primary"
          onClick={() => onSubmit("approve", entry)}
          disabled={submitting}
        >
          {submitting && state.action === "approve"
            ? "Approving…"
            : "Approve access"}
        </button>
        <button
          className="secondary"
          onClick={() => onSubmit("reject", entry)}
          disabled={submitting}
        >
          {submitting && state.action === "reject" ? "Rejecting…" : "Reject"}
        </button>
      </div>
      <p className="muted" style={{ marginTop: 12, fontSize: 13 }}>
        This link is one-shot — once you click, it can't be used again.
      </p>
    </>
  );
}

function RequestCard({ entry }: { entry: AdminPreviewResponse }) {
  return (
    <div className="meta-cell" style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <img
          src={`https://github.com/${encodeURIComponent(entry.gh_login)}.png?size=64`}
          alt=""
          width={48}
          height={48}
          style={{ borderRadius: "50%" }}
        />
        <div>
          <div>
            <strong>@{entry.gh_login}</strong>
            {entry.name && (
              <span className="muted" style={{ marginLeft: 8 }}>
                ({entry.name})
              </span>
            )}
          </div>
          {entry.email && (
            <div className="muted" style={{ fontSize: 13 }}>
              {entry.email}
            </div>
          )}
          <div className="muted" style={{ fontSize: 13 }}>
            requested {formatDate(entry.requested_at)}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
